import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getWorkspaceGetArtifactContentQueryKey,
  workspaceGetArtifactContent,
} from '@/generated/hooks/rain/v1/workspace_scoped/thread_service';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/**
 * Always refetch on open. `artifact_edit` rewrites the SAME uri, which is the
 * whole query key, so any staleness here serves the pre-edit document for a
 * file the agent has since changed — with nothing in the UI to say so.
 * `fetchQuery` still dedupes concurrent calls, so a double click is one request.
 */
const ARTIFACT_CONTENT_STALE_TIME_MS = 0;

/**
 * Fetches an agent-written artifact's bytes over the WORKSPACE-scoped route —
 * `/api/v1/organizations/{org}/workspaces/{ws}/agents/{agentId}/threads/{threadId}/artifacts/{uri}:content`.
 *
 * Scoped, not the agent-scoped `/api/v1/agents/...` variant, because that bare
 * shape cannot reach Rain from a browser:
 *
 *  - No route. The cloudburst HTTPRoute is only
 *    `^/api/v1/organizations/[^/]+/workspaces/[^/]+/agents([/:].*)?$` — in gitops
 *    for dev/stg/prod and in the local transwarp mirror alike. Bare
 *    `/api/v1/agents/...` is routed for the named tempest agents (smith, quill2,
 *    astral2) only, so an agent-builder agent matches nothing and the request
 *    falls through to the SPA catch-all — HTML, status 200.
 *  - No permission. Every Rain PermissionTemplate is org/workspace-scoped, so
 *    Rakshak has no rule that matches a bare path and denies it outright.
 *
 * The artifact URI itself carries no org or workspace (`scratch://file.md`), but
 * that is independent of how the route is addressed: the scoped route reads the
 * agent's own scratch and workspace stores just the same.
 *
 * Reads through the React Query cache (`fetchQuery`), so reopening a file the
 * user already viewed is instant and a second click while one is in flight
 * joins the same request instead of starting another.
 */
export function useAgentArtifactContent(agentId: string, threadId: string) {
  const queryClient = useQueryClient();
  // Same source the rest of the feature's workspace-scoped calls read from
  // (see `use-agent-builder-api`), so the viewer stays on the org and workspace
  // the builder is already pointed at.
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);

  return useCallback(
    async (artifactUri: string): Promise<string> => {
      const encodedUri = encodeURIComponent(artifactUri);

      const response = await queryClient.fetchQuery({
        queryKey: getWorkspaceGetArtifactContentQueryKey(
          organization,
          workspace,
          agentId,
          threadId,
          encodedUri,
        ),
        queryFn: () =>
          workspaceGetArtifactContent(
            organization,
            workspace,
            agentId,
            threadId,
            encodedUri,
          ),
        staleTime: ARTIFACT_CONTENT_STALE_TIME_MS,
      });

      // A 200 is not proof the API answered. Anything that misses Rain's routes
      // is served the SPA shell — an HTML page, status 200 — and a gateway that
      // normalizes `%2F` away can turn a valid path into one that misses (see
      // `escapedSlashesAction` / `preserveEscapedSlashes` on the Envoy gateway).
      // Naming that case is the difference between "could not open this file"
      // and a viewer rendering an empty document.
      const payload: unknown = response.data;
      const content =
        typeof payload === 'object' && payload !== null
          ? (payload as { data?: string }).data
          : undefined;
      if (!content) {
        throw new Error(
          `Artifact content response carried no data for ${artifactUri} ` +
            `(content-type: ${String(response.headers['content-type'])})`,
        );
      }
      // `data` is typed `string` but carries base64-encoded bytes on the wire.
      return content;
    },
    [agentId, organization, queryClient, threadId, workspace],
  );
}
